import { useState } from 'react'
import { X, Upload, Wand2, Plus, Trash2 } from 'lucide-react'
import { Button } from '../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Label } from '../ui/label'
import { Badge } from '../ui/badge'
import { Checkbox } from '../ui/checkbox'
import { apiClient } from '../../lib/api'
import { BOOK_FILE_FORMATS } from '../../lib/constants'
import { SingleLanguageSelector } from '../shared/SingleLanguageSelector'

interface Book {
  id: string
  title_international: string
  author_international: string
}

interface BulkTranslationDialogProps {
  books: Book[]
  onClose: () => void
  onSuccess: () => void
}

interface TranslationAction {
  id: string
  language: string
  cefrLevel: string | null
  type: 'auto' | 'upload'
  file?: File
}

const CEFR_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2', 'Original']

export function BulkTranslationDialog({ books, onClose, onSuccess }: BulkTranslationDialogProps) {
  const [actions, setActions] = useState<TranslationAction[]>([])
  const [showAddAction, setShowAddAction] = useState(false)
  const [newLanguage, setNewLanguage] = useState('')
  const [newCefrLevel, setNewCefrLevel] = useState<string>('A1')
  const [newActionType, setNewActionType] = useState<'auto' | 'upload'>('auto')
  const [submitting, setSubmitting] = useState(false)
  const [skipDuplicates, setSkipDuplicates] = useState(true)

  const addAction = () => {
    if (!newLanguage) {
      alert('Please select a language')
      return
    }

    const cefrLevel = newCefrLevel === 'Original' ? null : newCefrLevel

    setActions([
      ...actions,
      {
        id: Math.random().toString(),
        language: newLanguage,
        cefrLevel,
        type: newActionType,
      },
    ])

    setShowAddAction(false)
    setNewLanguage('')
    setNewCefrLevel('A1')
    setNewActionType('auto')
  }

  const removeAction = (actionId: string) => {
    setActions(actions.filter((a) => a.id !== actionId))
  }

  const updateActionFile = (actionId: string, file: File) => {
    setActions(
      actions.map((a) =>
        a.id === actionId ? { ...a, file } : a
      )
    )
  }

  const handleSubmit = async () => {
    if (actions.length === 0) {
      alert('Please add at least one translation action')
      return
    }

    // Validate upload actions have files
    const uploadActions = actions.filter((a) => a.type === 'upload')
    if (uploadActions.some((a) => !a.file)) {
      alert('All upload actions must have a file selected')
      return
    }

    setSubmitting(true)
    let successCount = 0
    let skippedCount = 0
    const errors: string[] = []

    try {
      // Separate auto and upload actions
      const autoActions = actions.filter((a) => a.type === 'auto' && a.cefrLevel)
      const uploadActionsWithFiles = actions.filter((a) => a.type === 'upload')

      // Process auto-translations using bulk API
      if (autoActions.length > 0) {
        try {
          const result = await apiClient.bulkQueueTranslations({
            book_ids: books.map((b) => b.id),
            translations: autoActions.map((a) => ({
              language: a.language,
              cefr_level: a.cefrLevel!,
            })),
            skip_duplicates: skipDuplicates,
          })
          successCount += result.queued_count
          skippedCount += result.skipped_count || 0
        } catch (error: any) {
          console.error('Failed to queue auto-translations:', error)
          errors.push(`Auto-translations: ${error.message}`)
        }
      }

      // Process upload actions individually (can't be batched)
      for (const book of books) {
        for (const action of uploadActionsWithFiles) {
          try {
            if (!action.file) continue
            await apiClient.addTranslation({
              book_id: book.id,
              language: action.language,
              cefr_level: action.cefrLevel,
              file: action.file,
            })
            console.log(`✓ Uploaded translation for "${book.title_international}": ${action.language} ${action.cefrLevel || 'Original'}`)
            successCount++
          } catch (error: any) {
            console.error(`Failed to upload for ${book.title_international}:`, error)
            errors.push(`${book.title_international} - ${action.language} ${action.cefrLevel || 'Original'}: ${error.message}`)
          }
        }
      }

      // Show results
      const skippedMsg = skippedCount > 0 ? `\n${skippedCount} skipped (already exist).` : ''
      if (errors.length > 0 && successCount > 0) {
        alert(`${successCount} translation(s) added successfully.${skippedMsg}\n\n${errors.length} failed:\n${errors.join('\n')}\n\nCheck console for details.`)
      } else if (errors.length > 0) {
        alert(`All translations failed:\n${errors.join('\n')}\n\nCheck console for details.`)
      } else {
        alert(`Successfully added ${successCount} translation(s)!${skippedMsg} Auto-translations are queued and will be processed.`)
      }

      onSuccess()
    } catch (error) {
      console.error('Failed to submit bulk translations:', error)
      alert('Failed to submit translations. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="bulk-translation-dialog fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="dialog-content bg-background rounded-lg max-w-3xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="dialog-header flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-bold">Add Translations to {books.length} Book{books.length > 1 ? 's' : ''}</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Add translations to multiple books at once
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Content */}
        <div className="dialog-body flex-1 overflow-y-auto p-6 space-y-6">
          {/* Selected Books */}
          <div className="selected-books-section">
            <h3 className="text-sm font-semibold mb-3">Selected Books:</h3>
            <div className="space-y-2">
              {books.map((book) => (
                <div key={book.id} className="book-item p-2 bg-muted/30 rounded border border-border">
                  <p className="text-sm font-medium line-clamp-1">{book.title_international}</p>
                  <p className="text-xs text-muted-foreground line-clamp-1">{book.author_international}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Translation Actions */}
          <div className="translation-actions-section">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold">Translation Actions:</h3>
              <Button size="sm" onClick={() => setShowAddAction(true)}>
                <Plus className="mr-2 h-3 w-3" />
                Add Action
              </Button>
            </div>

            {actions.length === 0 ? (
              <div className="empty-state text-center py-8 border-2 border-dashed border-border rounded-lg">
                <p className="text-sm text-muted-foreground mb-3">No translation actions yet</p>
                <Button onClick={() => setShowAddAction(true)} size="sm">
                  <Plus className="mr-2 h-3 w-3" />
                  Add First Action
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {actions.map((action) => (
                  <div key={action.id} className="action-item flex items-center gap-3 p-3 border border-border rounded-lg bg-muted/20">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        {action.type === 'auto' ? (
                          <Wand2 className="h-4 w-4 text-purple-500" />
                        ) : (
                          <Upload className="h-4 w-4 text-primary" />
                        )}
                        <span className="text-sm font-medium">
                          {action.language} - {action.cefrLevel || 'Original'}
                        </span>
                        <Badge variant="outline" className="text-xs">
                          {action.type === 'auto' ? 'Auto-translate' : 'Upload'}
                        </Badge>
                      </div>
                      {action.type === 'upload' && (
                        <div className="mt-2">
                          {action.file ? (
                            <p className="text-xs text-muted-foreground">File: {action.file.name}</p>
                          ) : (
                            <div className="file-upload-input">
                              <input
                                type="file"
                                id={`file-${action.id}`}
                                accept={BOOK_FILE_FORMATS.acceptString}
                                className="hidden"
                                onChange={(e) => {
                                  const file = e.target.files?.[0]
                                  if (file) updateActionFile(action.id, file)
                                }}
                              />
                              <label
                                htmlFor={`file-${action.id}`}
                                className="inline-flex items-center gap-2 px-3 py-1.5 text-xs border border-border rounded hover:bg-muted cursor-pointer"
                              >
                                <Upload className="h-3 w-3" />
                                Select File
                              </label>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeAction(action.id)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add Action Form */}
          {showAddAction && (
            <Card className="add-action-form border-primary">
              <CardHeader>
                <CardTitle className="text-base">Add Translation Action</CardTitle>
                <CardDescription>This action will be applied to all selected books</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="form-field">
                  <Label>Language</Label>
                  <SingleLanguageSelector
                    value={newLanguage}
                    onChange={setNewLanguage}
                  />
                </div>
                <div className="form-field">
                  <Label>CEFR Level</Label>
                  <select
                    value={newCefrLevel}
                    onChange={(e) => setNewCefrLevel(e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                  >
                    {CEFR_LEVELS.map((level) => (
                      <option key={level} value={level}>
                        {level}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-field">
                  <Label>Translation Type</Label>
                  <div className="flex gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="action-type"
                        value="auto"
                        checked={newActionType === 'auto'}
                        onChange={(e) => setNewActionType(e.target.value as 'auto' | 'upload')}
                        disabled={newCefrLevel === 'Original'}
                      />
                      <span className="text-sm">Auto-translate</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="action-type"
                        value="upload"
                        checked={newActionType === 'upload'}
                        onChange={(e) => setNewActionType(e.target.value as 'auto' | 'upload')}
                      />
                      <span className="text-sm">Upload file</span>
                    </label>
                  </div>
                  {newCefrLevel === 'Original' && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Original level only supports file upload
                    </p>
                  )}
                </div>
                <div className="form-actions flex gap-2 justify-end">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowAddAction(false)
                      setNewLanguage('')
                      setNewCefrLevel('A1')
                      setNewActionType('auto')
                    }}
                  >
                    Cancel
                  </Button>
                  <Button onClick={addAction}>Add Action</Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Footer */}
        <div className="dialog-footer p-6 border-t border-border space-y-4">
          {/* Skip Duplicates Option */}
          <div className="skip-duplicates-option flex items-center space-x-2 p-3 bg-muted rounded-lg">
            <Checkbox 
              id="skip-duplicates"
              checked={skipDuplicates}
              onCheckedChange={(checked) => setSkipDuplicates(checked === true)}
            />
            <Label htmlFor="skip-duplicates" className="text-sm cursor-pointer">
              Skip existing translations
            </Label>
            <span className="text-xs text-muted-foreground ml-2">
              (Check existing translations before queueing - slower but prevents duplicates)
            </span>
          </div>

          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {actions.length} action{actions.length !== 1 ? 's' : ''} × {books.length} book{books.length !== 1 ? 's' : ''} = {actions.length * books.length} total operations
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} disabled={submitting}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={submitting || actions.length === 0}>
                {submitting ? 'Processing...' : 'Submit All'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

